import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { Notification } from '../../../shared/models/notification.model';
import { UtilsService } from 'shared-lib';
import { NotificationService } from '../../../shared/services/notification.service';
import { take } from 'rxjs';
import { Router } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';

@Component({
  selector: 'race-notification-content',
  imports: [DatePipe, NzSpinModule, NzDrawerModule, NzIconModule, TranslatePipe],
  template: `
    <div class="notification-content">
      <nz-spin [nzSpinning]="isLoading()" [nzTip]="'notificationList.loading' | translate">
        <ul class="notification-list">
          @for (data of notificationService.notificationList().slice(0, 10); track data.id) {
            <li class="notification-item" [class.unread]="!data.seen">
              <button class="content" type="button" (click)="openModal(data)" (keydown.enter)="openModal(data)">
                <div class="item-header">
                  <p class="title mb-0 fw-600">{{ data.title }}</p>
                  @if (!data.seen) {
                    <span aria-hidden="true" class="dot"></span>
                  }
                </div>
                <p class="body mb-0">{{ data.body }}</p>
                <span class="date">{{ data.createdDate | date: 'MMM d, y, h:mm a' }}</span>
              </button>
            </li>
          } @empty {
            <div class="d-flex justify-content-center align-items-center flex-column no-notification w-100">
              <i class="no-notification-icon" nz-icon nzTheme="outline" nzType="bell"></i>
              <p>{{ 'notificationList.emptyUnread' | translate }}</p>
            </div>
          }
        </ul>
      </nz-spin>
    </div>
  `,
  styleUrls: ['./notification-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationContentComponent {
  readonly isLoading = input<boolean>(false);

  readonly closeDrawer = output<boolean>();

  private readonly utilsService = inject(UtilsService);
  private readonly router = inject(Router);
  readonly notificationService = inject(NotificationService);

  openModal(data: Notification): void {
    let wasUnseen = false;
    this.notificationService.notificationList.update((list) =>
      list.map((item) => {
        if (item.id === data.id) {
          wasUnseen = !item.seen;
          return { ...item, seen: true };
        }
        return item;
      })
    );

    if (wasUnseen) {
      this.notificationService.notificationCount.update((count) => (count > 0 ? count - 1 : count));
      this.updateNotificationState(data);
    }

    if (data.actionPathWeb) {
      this.closeDrawer.emit(true);
      this.router.navigate([data.actionPathWeb]);
    } else {
      this.utilsService.showNotificationDetails({
        title: data.title,
        body: data.body,
        createdAt: data.createdDate,
      });
    }
  }

  updateNotificationState(data: Notification): void {
    this.notificationService
      .markAsRead(data.id)
      .pipe(take(1))
      .subscribe({
        next: () => {
          // Optionally, refresh the notification list or update the UI
        },
        error: (error) => {
          this.utilsService.showError('notificationList.error.markAsReadFailed', error);
        },
      });
  }
}
